import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid' && data.order_id) {
        setStatus('success');
        setOrderData(data);
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-16" data-testid="checkout-success-page">
      <div className="max-w-xl mx-auto px-4">
        {/* Checking Status */}
        {status === 'checking' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-amber-600 animate-spin" />
            </div>
            <h1 className="text-3xl font-black text-stone-900 mb-4">
              Processing Your Order
            </h1>
            <p className="text-stone-600 mb-2">
              Please wait while we confirm your payment...
            </p>
            <p className="text-sm text-stone-400">
              Attempt {attempts + 1} of {maxAttempts}
            </p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="text-center" data-testid="payment-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-black text-stone-900 mb-4">
              Order Confirmed!
            </h1>
            <p className="text-stone-600 mb-6">
              Thank you for your purchase. Your order has been placed successfully.
            </p>
            
            {orderData?.order_number && (
              <div className="bg-white p-6 rounded-lg shadow-lg mb-8 border-l-4 border-green-500">
                <p className="text-sm text-stone-500 mb-1">Order Number</p>
                <p className="text-2xl font-black text-stone-900">{orderData.order_number}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {orderData?.order_id && (
                <Link to={`/order/${orderData.order_id}/success`}>
                  <Button className="bg-amber-600 hover:bg-amber-700 gap-2">
                    <Package className="h-4 w-4" />
                    View Order Details
                  </Button>
                </Link>
              )}
              <Link to="/shop/iphone-cases">
                <Button variant="outline" className="gap-2">
                  Continue Shopping
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Error/Expired/Timeout */}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="text-center" data-testid="payment-error">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-black text-stone-900 mb-4">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-stone-600 mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : status === 'timeout'
                  ? 'We couldn\'t confirm your payment. Please check your email or contact support.'
                  : 'We encountered an issue processing your order. Please try again or contact support.'
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart">
                <Button className="bg-amber-600 hover:bg-amber-700">
                  Return to Cart
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline">
                  Go Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
